import * as v from 'valibot';

// ============================================================================
// MMT Base Response Types (Common to all onboarding APIs)
// ============================================================================

/**
 * Response status enum - matches backend ResponseStatus
 */
export const ResponseStatusSchema = v.picklist(['SUCCESS', 'FAILED']);

export type ResponseStatus = v.InferOutput<typeof ResponseStatusSchema>;

/**
 * MMT Error Details schema - matches backend MMTErrorDetails
 */
export const MMTErrorDetailsSchema = v.object({
  errorCode: v.optional(v.string()),
  errorMessage: v.optional(v.string()),
  errorDescription: v.optional(v.string()),
  serviceName: v.optional(v.string()),
  statusCode: v.optional(v.number()),
  stackTrace: v.optional(v.string()),
  owner: v.optional(v.string()),
});

export type MMTErrorDetails = v.InferOutput<typeof MMTErrorDetailsSchema>;

/**
 * Custom error class for onboarding API errors
 * Thrown when responseStatus is FAILED
 */
export class OnboardingApiError extends Error {
  constructor(
    message: string,
    public readonly errorDetails?: MMTErrorDetails,
    public readonly statusCode?: number,
  ) {
    super(message);
    this.name = 'OnboardingApiError';
  }
}

// ============================================================================
// Destination Suggestion Types (Step 1)
// ============================================================================

/**
 * Destination suggestion schema for auto-suggest results.
 * All data is pre-formatted and render-ready.
 * imageUrls is an array - popular destinations have 1 image, globe destinations have 3 images
 */
export const DestinationSuggestionSchema = v.object({
  // Unique identifier for the destination
  id: v.string(),
  // Destination name (e.g., "Ho Chi Minh City")
  name: v.string(),
  // Array of image URLs (1 for popular destinations, 3 for globe destinations)
  imageUrls: v.array(v.string()),
  // Pre-formatted description of what the destination is famous for
  famousFor: v.string(), // e.g., "Stunning natural beauty, flavorful cuisine, rich & vibrant culture"
  // Country name
  country: v.string(),
  // Formatted display name for UI
  displayName: v.string(), // e.g., "Ho Chi Minh City, Vietnam"
});

export type DestinationSuggestion = v.InferOutput<typeof DestinationSuggestionSchema>;

/**
 * Preference option schema for any type of preference selection.
 *
 * All fields except `id` and `label` are optional, making this schema
 * flexible enough to represent any type of option (traveller type, duration,
 * month, experience, etc.) without requiring separate schemas.
 */
export const PreferenceOptionSchema = v.looseObject({
  // Unique identifier for this option
  id: v.string(),
  // Display label for the option (e.g., "SOLO", "5 Days", "November")
  label: v.string(),
  // Optional icon URL(s) for visual representation
  // Can be a single icon or multiple (e.g., for image carousels in month selection)
  iconUrl: v.optional(v.union([v.string(), v.array(v.string())])),
  // Optional description or additional info text
  description: v.optional(v.string()),
  // Optional temperature range (for month options)
  temperature: v.optional(v.string()),
  // Optional month (for month options)
  month: v.optional(v.string()),
  // Optional duration (for duration options)
  duration: v.optional(v.number()),
});

export type PreferenceOption = v.InferOutput<typeof PreferenceOptionSchema>;

/**
 * Day option schema for flexible date selection
 */
export const DayOptionSchema = v.object({
  id: v.number(),
  label: v.string(),
});

export type DayOption = v.InferOutput<typeof DayOptionSchema>;

/**
 * Month option schema for flexible date selection
 */
export const MonthOptionSchema = v.object({
  id: v.string(),
  label: v.string(),
});

export type MonthOption = v.InferOutput<typeof MonthOptionSchema>;

/**
 * Date selector configuration schema for calendar/flexible date selection
 */
export const DateSelectorConfigSchema = v.object({
  // Title shown in the popup header
  popupTitle: v.string(),
  // Placeholder text when no selection
  placeholder: v.string(),
  // Label for "Dates" mode tab
  datesModeLabel: v.string(),
  // Label for "Flexible" mode tab
  flexibleModeLabel: v.string(),
  // Label for days section
  daysLabel: v.string(),
  // Label for months section
  monthsLabel: v.string(),
  // Placeholder for days input
  daysInputPlaceholder: v.string(),
  // Confirm button text
  confirmButtonText: v.string(),
  // Day options to display
  dayOptions: v.array(DayOptionSchema),
  // Month options to display
  monthOptions: v.array(MonthOptionSchema),
});

export type DateSelectorConfig = v.InferOutput<typeof DateSelectorConfigSchema>;

/**
 * Preference question schema - represents a single question in the onboarding flow.
 * This is the main building block for dynamic preference questions.
 *
 * The frontend uses the `category` field to determine which UI component to render:
 * - "option_selector" → OptionSelector component (for traveller type, experience type, etc.)
 * - "date_selector" → DateSelector component (for travel dates with calendar/flexible selection)
 */
export const PreferenceQuestionSchema = v.object({
  id: v.string(),
  // The question text to display (e.g., "Who's joining on this trip?")
  question: v.string(),
  // Category determines which UI component renders this question
  category: v.picklist(['option_selector', 'date_selector']),
  // Whether multiple options can be selected (for option_selector)
  multiSelect: v.optional(v.boolean()),
  // Array of options/choices for this question (for option_selector)
  data: v.array(PreferenceOptionSchema),
  // Configuration for date selector (required when category is 'date_selector')
  dateSelectorConfig: v.optional(DateSelectorConfigSchema),
});

export type PreferenceQuestion = v.InferOutput<typeof PreferenceQuestionSchema>;

// ============================================================================
// Onboarding Preferences Types (Request)
// ============================================================================

/**
 * Onboarding preferences schema for submitting user selections from Step 1.
 * Sent to backend to get personalized recommendations for Steps 2 and 3.
 */
export const OnboardingPreferencesSchema = v.object({
  // Array of selected destination IDs from Step 1
  selectedDestinations: v.array(v.string()),
  // Optional locale for formatting (IETF BCP 47: 'en-US', 'hi-IN', etc.)
  locale: v.optional(v.string()),
});

export type OnboardingPreferences = v.InferOutput<typeof OnboardingPreferencesSchema>;

// ============================================================================
// Onboarding Recommendations Types (Response)
// ============================================================================

export const OnboardingRecommendationsSchema = v.array(PreferenceQuestionSchema);

export type OnboardingRecommendations = v.InferOutput<typeof OnboardingRecommendationsSchema>;

// ============================================================================
// Globe Destination Types (Config)
// ============================================================================

/**
 * Globe destination schema for map visualization.
 * Combines destination data with geographic coordinates.
 */
export const GlobeDestinationSchema = v.object({
  // Destination information
  destination: DestinationSuggestionSchema,
  // Geographic coordinates for map placement
  coordinates: v.object({
    lat: v.number(), // Latitude
    lng: v.number(), // Longitude
  }),
});

export type GlobeDestination = v.InferOutput<typeof GlobeDestinationSchema>;

// ============================================================================
// Onboarding Config Types (Initial Load)
// ============================================================================

export const OnboardingHeadingSchema = v.object({
  // Personalized greeting (e.g., "Hello Vineel")
  greeting: v.string(),
  // Main question (e.g., "Where would you like to travel?")
  question: v.string(),
});

export type OnboardingHeading = v.InferOutput<typeof OnboardingHeadingSchema>;

/**
 * Onboarding config schema for initial app load.
 * Contains all static/dynamic data needed for the onboarding flow.
 */
export const OnboardingConfigSchema = v.object({
  // Heading section with personalized greeting
  heading: OnboardingHeadingSchema,
  // Popular destinations to show when search query is empty
  popularDestinations: v.array(DestinationSuggestionSchema),
  // Randomized destinations for globe/map visualization
  globeDestinations: v.array(GlobeDestinationSchema),
});

export type OnboardingConfig = v.InferOutput<typeof OnboardingConfigSchema>;

// ============================================================================
// Onboarding Resource Interface
// ============================================================================

/**
 * Onboarding Resource Interface
 * Defines the contract for onboarding flow interactions
 */
export interface OnboardingResource {
  /**
   * Get auto-suggest results for destination search
   * @param query - Search query string (empty string returns popular destinations)
   * @returns Promise resolving to array of destination suggestions
   */
  autoSuggest(query: string): Promise<DestinationSuggestion[]>;

  /**
   * Submit user preferences from Step 1 and get personalized recommendations for Steps 2 & 3
   * @param preferences - User's selected destinations and locale
   * @returns Promise resolving to personalized recommendations
   */
  submitPreferences(preferences: OnboardingPreferences): Promise<OnboardingRecommendations>;

  /**
   * Get onboarding configuration for initial load
   * @returns Promise resolving to config with headings, popular destinations, and globe data
   */
  getConfig(): Promise<OnboardingConfig>;
}

// ============================================================================
// Trip Plan Payload Types (Make me a Plan request)
// ============================================================================

/**
 * Travel dates schema - supports both specific dates and flexible dates
 */
export const TravelDatesSchema = v.object({
  // For specific dates
  startDate: v.optional(v.string()), // ISO date "2026-02-15"
  endDate: v.optional(v.string()), // ISO date "2026-02-20"
  // For flexible dates
  flexibleDays: v.optional(v.number()), // e.g., 5
  flexibleMonth: v.optional(v.string()), // e.g., "February"
});

export type TravelDates = v.InferOutput<typeof TravelDatesSchema>;

/**
 * Travel preferences schema
 */
export const TravelPreferencesSchema = v.object({
  travellerType: v.optional(v.string()), // "solo" | "couple" | "family" | "friends" | "others"
  experienceTypes: v.optional(v.array(v.string())), // ["cultural_history", "nature", "adventure"]
  travelPace: v.optional(v.string()), // "ambitious" | "moderate" | "relaxed"
  accommodation: v.optional(v.string()), // "comfort" | "premium" | "luxury"
  daysRhythm: v.optional(v.string()), // "early_start" | "late_nights"
});

export type TravelPreferences = v.InferOutput<typeof TravelPreferencesSchema>;

/**
 * Trip Plan Payload schema - sent when user clicks "Make me a Plan"
 */
export const TripPlanPayloadSchema = v.object({
  // Request and conversation identifiers
  request_id: v.string(), // e.g., "req_789"
  conversation_id: v.string(), // e.g., "conv_012"
  // Destination names as simple strings
  destinations: v.array(v.string()), // e.g., ["Goa", "Mumbai"]
  // Travel dates (optional)
  travelDates: v.optional(TravelDatesSchema),
  // Travel preferences (optional)
  preferences: v.optional(TravelPreferencesSchema),
});

export type TripPlanPayload = v.InferOutput<typeof TripPlanPayloadSchema>;

/**
 * Generate Trip Query Response schema - response from /generate-trip-query endpoint
 * Contains the rephrased query to be sent to chat
 */
export const GenerateTripQueryResponseSchema = v.object({
  // The rephrased query message to be sent to chat
  queryMessage: v.string(),
  // Request ID for tracking (echoed back from request)
  request_id: v.optional(v.string()),
  // Conversation ID for session tracking (echoed back from request)
  conversation_id: v.optional(v.string()),
});

export type GenerateTripQueryResponse = v.InferOutput<typeof GenerateTripQueryResponseSchema>;

// ============================================================================
// API Response Wrappers (MMTBaseResponse format)
// ============================================================================

/**
 * Auto-suggest API Response schema - wraps destination suggestions
 * Response from GET /api/onboarding/auto-suggest
 */
export const AutoSuggestApiResponseSchema = v.object({
  responseStatus: ResponseStatusSchema,
  errorDetails: v.optional(MMTErrorDetailsSchema),
  suggestions: v.optional(v.array(DestinationSuggestionSchema)),
});

export type AutoSuggestApiResponse = v.InferOutput<typeof AutoSuggestApiResponseSchema>;

/**
 * Preferences API Response schema - wraps preference questions
 * Response from POST /api/onboarding/preferences
 */
export const PreferencesApiResponseSchema = v.object({
  responseStatus: ResponseStatusSchema,
  errorDetails: v.optional(MMTErrorDetailsSchema),
  questions: v.optional(v.array(PreferenceQuestionSchema)),
});

export type PreferencesApiResponse = v.InferOutput<typeof PreferencesApiResponseSchema>;

/**
 * Config API Response schema - wraps onboarding config
 * Response from GET /api/onboarding/config
 */
export const ConfigApiResponseSchema = v.object({
  responseStatus: ResponseStatusSchema,
  errorDetails: v.optional(MMTErrorDetailsSchema),
  heading: v.optional(OnboardingHeadingSchema),
  popularDestinations: v.optional(v.array(DestinationSuggestionSchema)),
  globeDestinations: v.optional(v.array(GlobeDestinationSchema)),
});

export type ConfigApiResponse = v.InferOutput<typeof ConfigApiResponseSchema>;

/**
 * Generate Trip Query API Response schema - wraps trip query response
 * Response from POST /api/onboarding/generate-trip-query
 */
export const GenerateTripQueryApiResponseSchema = v.object({
  responseStatus: ResponseStatusSchema,
  errorDetails: v.optional(MMTErrorDetailsSchema),
  queryMessage: v.optional(v.string()),
  request_id: v.optional(v.string()),
  conversation_id: v.optional(v.string()),
});

export type GenerateTripQueryApiResponse = v.InferOutput<typeof GenerateTripQueryApiResponseSchema>;

// ============================================================================
// Error Types
// ============================================================================

/**
 * Validation error thrown when onboarding input is invalid
 */
export class OnboardingValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: Array<{ path: string[]; message: string }>,
  ) {
    super(message);
    this.name = 'OnboardingValidationError';
  }
}
